// QuestionsPage.js
// Logic-driven multi-step form that saves progress to Firebase after each step

import React, { useState, useEffect, useRef } from "react";
import { updateDoc, serverTimestamp, doc } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import { db } from "./firebase";
import { getDoc } from "firebase/firestore";
import calculatePreQualification from "./PreQual";
import generatePreQualpdf from "./generatePreQualpdf";
import uploadAndStorePreQualPDFs from "./uploadAndStorePreQualPDFs";
import { useNavigate } from "react-router-dom";
import { frequencyToMonthly, formatCurrency } from "./formUtils";
import "./QuestionsPage.css";
import { getExcludedSteps, isStepComplete } from "./formUtils";
import LanguageToggle from "./LanguageToggle";

const containerStyle = {
  backgroundColor: "#fff",
  borderRadius: "16px",
  padding: "8vw 4vw",
  width: "100%",
  maxWidth: "500px",
  margin: "8vw auto",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  fontFamily: "Arial, sans-serif",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  boxSizing: "border-box",
};

const inputStyle = {
  width: "100%",
  padding: "12px",
  margin: "20px 0",
  borderRadius: "8px",
  border: "1px solid #ccc",
  fontSize: "16px",
  boxSizing: "border-box",
};

const smallInputStyle = {
  width: "48%",
  padding: "12px",
  margin: "10px 1%",
  borderRadius: "8px",
  border: "1px solid #ccc",
  fontSize: "16px",
  boxSizing: "border-box",
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
};

const buttonStyle = {
  backgroundColor: "#0096b2",
  color: "#fff",
  border: "none",
  padding: "14px",
  width: "100%",
  fontWeight: "bold",
  fontSize: "16px",
  marginTop: "10px",
  marginBottom: "10px",
  borderRadius: "8px",
  cursor: "pointer",
};

const QuestionCard = ({ title, children, onContinue, onBack }) => (
  <div className="qp-container" style={containerStyle}>
    <h2 style={{ textAlign: "center" }}>{title}</h2>
    {children}
    <div
      className="qp-btns"
      style={{ display: "flex", gap: 12, width: "100%" }}
    >
      {onContinue && (
        <button style={buttonStyle} onClick={onContinue}>
          Continue
        </button>
      )}
      {onBack && (
        <button style={buttonStyle} onClick={onBack}>
          Back
        </button>
      )}
    </div>
  </div>
);

const ALL_STEP_NUMBERS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
];

const QuestionsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [docId, setDocId] = useState(null);
  const [step, setStep] = useState(1);
  const [formDataPurchase, setFormDataPurchase] = useState({});
  const [selectedDebts, setSelectedDebts] = useState([]);
  const [previousSelectedDebts, setPreviousSelectedDebts] = useState([]);
  const [previousDebtFormData, setPreviousDebtFormData] = useState({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [history, setHistory] = useState([1]);
  const [monthlyIncome, setMonthlyIncome] = useState(null);
  const [rawIncome, setRawIncome] = useState("");

  // Dynamically computed docRef
  const docRef = useRef(null);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const id = query.get("id") || localStorage.getItem("clientDocId");

    if (!id) {
      navigate("/login");
      return;
    }

    setDocId(id);
    localStorage.setItem("clientDocId", id);

    const ref = doc(db, "clients", id);
    docRef.current = ref;

    const fetchClientData = async () => {
      try {
        const snapshot = await getDoc(ref);
        if (snapshot.exists()) {
          const data = snapshot.data();
          if (data.formDataPurchase) {
            setFormDataPurchase(data.formDataPurchase);
            setSelectedDebts(data.formDataPurchase.selectedDebts || []);
            if (data.currentStepQ && data.currentStepQ > 1) {
              setStep(data.currentStepQ);
              setHistory([1, data.currentStepQ]);
            } else {
              setStep(1);
              setHistory([1]);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load client data:", error);
      }
    };

    fetchClientData();
  }, [location]);

  const handleDebtSelection = (e) => {
    const { value, checked } = e.target;
    setSelectedDebts((prev) =>
      checked ? [...prev, value] : prev.filter((item) => item !== value)
    );
    setFormDataPurchase((prev) => ({
      ...prev,
      selectedDebts: checked
        ? [...(prev.selectedDebts || []), value]
        : (prev.selectedDebts || []).filter((item) => item !== value),
    }));
  };

  const sanitize = (value) => {
    if (typeof value !== "string") return value;
    return value.replace(/[<>]/g, ""); // Strip < and >
  };

  function getNextStep(current, data) {
    const excluded = getExcludedSteps(data);
    let next = current + 1;
    while (excluded.includes(next) && next < 20) next += 1;
    return Math.min(next, 20);
  }

  function getPrevStep(current, data) {
    const excluded = getExcludedSteps(data);
    let prev = current - 1;
    while (excluded.includes(prev) && prev > 1) prev -= 1;
    return Math.max(prev, 1);
  }

  function validateStep(step, formDataPurchase, selectedDebts) {
    switch (step) {
      case 1:
        return !!formDataPurchase.location;
      case 2:
        if (!formDataPurchase.incomeFrequency || !formDataPurchase.income)
          return false;
        if (
          formDataPurchase.incomeFrequency === "Hourly" &&
          !formDataPurchase.hoursPerWeek
        )
          return false;
        return true;
      case 3:
        return !!formDataPurchase.maritalStatus;
      case 4:
        if (
          formDataPurchase.maritalStatus === "Married" &&
          !formDataPurchase.prenuptialAgreement
        )
          return false;
        return true;
      case 5:
        if (
          formDataPurchase.maritalStatus === "Married" &&
          formDataPurchase.prenuptialAgreement !== "Yes" &&
          (!formDataPurchase.spouseFirstName ||
            !formDataPurchase.spouseLastName ||
            !formDataPurchase.spousePhone ||
            !formDataPurchase.spouseEmail)
        )
          return false;
        return true;
      case 6:
        if (
          formDataPurchase.maritalStatus === "Married" &&
          formDataPurchase.prenuptialAgreement !== "Yes" &&
          (!formDataPurchase.spouseFrequency ||
            !formDataPurchase.spouseIncome ||
            (formDataPurchase.spouseFrequency === "Hourly" &&
              !formDataPurchase.spouseHoursPerWeek))
        )
          return false;
        return true;
      case 7:
        return !!formDataPurchase.employmentType;
      case 8:
        if (formDataPurchase.employmentType === "Retired") return true;
        return [">2", "<2"].includes(formDataPurchase.workExperienceDuration);
      case 9:
        if (formDataPurchase.employmentType === "Retired") return true;
        if (
          formDataPurchase.workExperienceDuration === "<2" &&
          !formDataPurchase.currentOccupation
        )
          return false;
        return true;
      case 10:
        if (formDataPurchase.employmentType === "Retired") return true;
        return !!formDataPurchase.occupation2Years;
      case 11:
        if (formDataPurchase.employmentType === "Retired") return true;
        if (
          formDataPurchase.occupation2Years === "No" &&
          !formDataPurchase.studentStatus
        )
          return false;
        return true;
      case 12:
        if (
          formDataPurchase.maritalStatus === "Married" &&
          formDataPurchase.prenuptialAgreement !== "Yes" &&
          !formDataPurchase.spouseEmploymentType
        ) {
          return false;
        }
        return true;
      case 13:
        if (
          formDataPurchase.maritalStatus === "Married" &&
          formDataPurchase.prenuptialAgreement !== "Yes"
        ) {
          return [">2", "<2"].includes(
            formDataPurchase.spouseWorkExperienceDuration
          );
        }
        return true;

      // --- Step 14 validation (debts) ---
      case 14: {
        if (!selectedDebts || selectedDebts.length === 0) return true;
        const toNum = (v) =>
          parseFloat((v || "").toString().replace(/[^0-9.]/g, "")) || 0;
        const debtFieldsMap = {
          autoLoan: "autoLoan",
          personalLoan: "personalLoan",
          studentLoan: "studentLoanMonthly",
          creditCards: "creditCard1",
          storeCards: "storeCards",
          furnitureLoans: "furnitureLoans",
          otherDebts: "otherDebts",
        };
        return selectedDebts.every((debt) => {
          if (debt === "creditCards") {
            return (
              toNum(formDataPurchase.creditCard1) ||
              toNum(formDataPurchase.creditCard2) ||
              toNum(formDataPurchase.creditCard3) ||
              toNum(formDataPurchase.creditCard4)
            );
          }
          if (debt === "studentLoan") {
            return (
              toNum(formDataPurchase.studentLoanMonthly) ||
              toNum(formDataPurchase.studentLoanTotal)
            );
          }
          return toNum(formDataPurchase[debtFieldsMap[debt]]);
        });
      }
      default:
        return true;
    }
  }
  
  // FORMAT HELPERS
  const formatCurrency = (value) => {
    const num = parseFloat(value.replace(/[^0-9.]/g, ""));
    if (isNaN(num)) return "";
    return num.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    });
  };

  const capitalizeName = (value) => {
    return value
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .replace(/\s+/g, " ")
      .trim();
  };

  const formatPhoneNumber = (value = "") => {
    const cleaned = value.replace(/\D/g, "").slice(0, 10);
    if (cleaned.length < 4) return cleaned;
    if (cleaned.length < 7)
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6
    )}`;
  };

  const toNum = (v) =>
    parseFloat((v || "").toString().replace(/[^0-9.]/g, "")) || 0;

  // MAIN INPUT CHANGE HANDLER
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "prenuptialAgreement" && value === "Yes") {
      setFormDataPurchase((prev) => {
        const cleared = { ...prev };
        [
          "spouseFirstName",
          "spouseLastName",
          "spousePhone",
          "spouseEmail",
          "spouseIncome",
          "spouseFrequency",
          "spouseHoursPerWeek",
          "spouseEmploymentType",
          "spouseEmploymentOtherDetail",
          "spouseWorkExperienceDuration",
          "spouseAutoLoan",
          "spousePersonalLoan",
          "spouseStudentLoanMonthly",
          "spouseStudentLoanTotal",
          "spouseCreditCard1",
          "spouseCreditCard2",
          "spouseCreditCard3",
          "spouseCreditCard4",
          "spouseStoreCards",
          "spouseFurnitureLoans",
          "spouseApplianceLoans",
          "spouseOtherDebts",
          "spouseCreditScore",
        ].forEach((f) => (cleared[f] = ""));
        return { ...cleared, [name]: value };
      });
      return;
    }
    if (name === "income" || name === "spouseIncome") {
      let val = value.replace(/[^0-9.]/g, "");
      // Limit to 1 dot, and 2 decimal places
      const parts = val.split(".");
      if (parts.length > 2) {
        val = parts[0] + "." + parts[1]; // Remove extra dots
      }
      if (val.includes(".")) {
        const [int, dec] = val.split(".");
        val = int + "." + (dec || "").slice(0, 2);
      }
      setFormDataPurchase((prev) => ({
        ...prev,
        [name]: val,
      }));
      return;
    }

    // Auto-clear hours if switching from Hourly to another
    if (name === "incomeFrequency" && value !== "Hourly") {
      setFormDataPurchase((prev) => ({
        ...prev,
        [name]: value,
        hoursPerWeek: "",
      }));
      return;
    }

    if (name === "spouseFrequency" && value !== "Hourly") {
      setFormDataPurchase((prev) => ({
        ...prev,
        [name]: value,
        spouseHoursPerWeek: "",
      }));
      return;
    }

    // Prevent unwanted characters
    if (name.includes("Phone") && /[^\d\s()-]/.test(value)) return;
    if (name.includes("Zip") && /[^\d]/.test(value)) return;
    if (name.toLowerCase().includes("name") && /[^a-zA-Z\s]/.test(value))
      return;

    setFormDataPurchase((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let formatted = value;

    if (name.includes("Phone")) {
      formatted = formatPhoneNumber(value);
    } else if (
      name.toLowerCase().includes("income") ||
      name.toLowerCase().includes("loan") ||
      name.toLowerCase().includes("price") ||
      name.toLowerCase().includes("debt") ||
      name.toLowerCase().includes("card")
    ) {
      formatted = formatCurrency(value);
    } else if (
      name.toLowerCase().includes("name") ||
      name.toLowerCase().includes("city")
    ) {
      formatted = capitalizeName(value);
    }

    setFormDataPurchase((prev) => ({ ...prev, [name]: formatted }));
  };

  useEffect(() => {
    if (!formDataPurchase.income || !formDataPurchase.incomeFrequency) return;

    const monthly = frequencyToMonthly(
      toNum(formDataPurchase.income),
      formDataPurchase.incomeFrequency,
      toNum(formDataPurchase.hoursPerWeek || 40)
    );

    setMonthlyIncome(monthly);
  }, [
    formDataPurchase.income,
    formDataPurchase.incomeFrequency,
    formDataPurchase.hoursPerWeek,
  ]);

  const canContinue = () => validateStep(step, formDataPurchase, selectedDebts);

  const handleContinue = async () => {
    if (!canContinue()) return;
    const next = getNextStep(step, formDataPurchase);

    const excluded = getExcludedSteps(formDataPurchase);
    const requiredSteps = ALL_STEP_NUMBERS.filter(
      (stepNum) => !excluded.includes(stepNum)
    );

    for (const s of requiredSteps) {
      if (!isStepComplete(s, formDataPurchase)) {
        setStep(s); // Jump to the first incomplete required step
        // Optionally:
        // setError(`Please complete: ${stepLabel(s)}`);
        return;
      }
    }

    const sanitizedformDataPurchase = Object.fromEntries(
      Object.entries(formDataPurchase).map(([k, v]) => [k, sanitize(v)])
    );
    const {
      AutoLoanBalance,
      CreditCardBalance,
      MortgageBalance,
      consolidatedFields,
      totalLoanRequested,
      spouseAutoLoanMonthly,
      spousePersonalLoanMonthly,
      ...purchaseCleaned
    } = sanitizedformDataPurchase;

    try {
      // Update stepHistory
      let stepHistory = [];
      const snapshot = await getDoc(docRef.current);
      if (snapshot.exists()) {
        const data = snapshot.data();
        stepHistory = Array.isArray(data.stepHistory)
          ? data.stepHistory
          : [step];
      } else {
        stepHistory = [step];
      }

      const idx = stepHistory.lastIndexOf(step);
      if (idx === -1) {
        stepHistory.push(step, next);
      } else {
        stepHistory = stepHistory.slice(0, idx + 1);
        if (stepHistory[stepHistory.length - 1] !== next)
          stepHistory.push(next);
      }

      await updateDoc(docRef.current, {
        formDataPurchase: purchaseCleaned,
        transactionType: "Purchase",
        currentStepQ: next,
        stepHistory,
        updatedAt: serverTimestamp(),
      });
      setStep(next);
      setHistory(stepHistory);
    } catch (err) {
      console.error("Failed to save progress:", err);
    }
  };

  const handleBack = async () => {
    const prev = getPrevStep(step, formDataPurchase);
    let stepHistory = [];
    const snapshot = await getDoc(docRef.current);
    if (snapshot.exists()) {
      const data = snapshot.data();
      stepHistory = Array.isArray(data.stepHistory) ? data.stepHistory : [1];
    } else {
      stepHistory = [1];
    }
    if (stepHistory.length > 1) stepHistory.pop();
    await updateDoc(docRef.current, { stepHistory });
    setStep(prev);
    setHistory(stepHistory);
  };

  const getControls = () => ({
    onContinue: canContinue() ? handleContinue : null,
    onBack: step > 1 ? handleBack : null,
  });

  const [showAdditionalIncome, setShowAdditionalIncome] = React.useState(
    !!formDataPurchase.additionalWorkIncome || !!formDataPurchase.bonusIncome
  );

  const [showSpouseAdditionalIncome, setShowSpouseAdditionalIncome] = React.useState(
    !!formDataPurchase.spouseAdditionalWorkIncome || !!formDataPurchase.spouseBonusIncome
  );

  const renderStep = () => {
    const isMarried = formDataPurchase.maritalStatus === "Married";
    const includeSpouse =
      formDataPurchase.maritalStatus === "Married" &&
      formDataPurchase.prenuptialAgreement !== "Yes";

    switch (step) {
      case 1:
        return (
          <QuestionCard
            title="Where do you want to purchase?"
            {...getControls()}
          >
            <select
              name="location"
              value={formDataPurchase.location || ""}
              onChange={handleChange}
              size={1}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                maxHeight: "150px",
                overflowY: "auto",
                appearance: "none",
              }}
            >
              <option value="">-- Select a County --</option>
              <option value="Adjuntas">Adjuntas</option>
              <option value="Aguada">Aguada</option>
              <option value="Aguadilla">Aguadilla</option>
              <option value="Aguas Buenas">Aguas Buenas</option>
              <option value="Aibonito">Aibonito</option>
              <option value="Añasco">Añasco</option>
              <option value="Arecibo">Arecibo</option>
              <option value="Arroyo">Arroyo</option>
              <option value="Barceloneta">Barceloneta</option>
              <option value="Barranquitas">Barranquitas</option>
              <option value="Bayamón">Bayamón</option>
              <option value="Cabo Rojo">Cabo Rojo</option>
              <option value="Caguas">Caguas</option>
              <option value="Camuy">Camuy</option>
              <option value="Canóvanas">Canóvanas</option>
              <option value="Carolina">Carolina</option>
              <option value="Cataño">Cataño</option>
              <option value="Cayey">Cayey</option>
              <option value="Ceiba">Ceiba</option>
              <option value="Ciales">Ciales</option>
              <option value="Cidra">Cidra</option>
              <option value="Coamo">Coamo</option>
              <option value="Comerío">Comerío</option>
              <option value="Corozal">Corozal</option>
              <option value="Culebra">Culebra</option>
              <option value="Dorado">Dorado</option>
              <option value="Fajardo">Fajardo</option>
              <option value="Florida">Florida</option>
              <option value="Guánica">Guánica</option>
              <option value="Guayama">Guayama</option>
              <option value="Guayanilla">Guayanilla</option>
              <option value="Guaynabo">Guaynabo</option>
              <option value="Gurabo">Gurabo</option>
              <option value="Hatillo">Hatillo</option>
              <option value="Hormigueros">Hormigueros</option>
              <option value="Humacao">Humacao</option>
              <option value="Isabela">Isabela</option>
              <option value="Jayuya">Jayuya</option>
              <option value="Juana Díaz">Juana Díaz</option>
              <option value="Juncos">Juncos</option>
              <option value="Lajas">Lajas</option>
              <option value="Lares">Lares</option>
              <option value="Las Marías">Las Marías</option>
              <option value="Las Piedras">Las Piedras</option>
              <option value="Loíza">Loíza</option>
              <option value="Luquillo">Luquillo</option>
              <option value="Manatí">Manatí</option>
              <option value="Maricao">Maricao</option>
              <option value="Maunabo">Maunabo</option>
              <option value="Mayagüez">Mayagüez</option>
              <option value="Moca">Moca</option>
              <option value="Morovis">Morovis</option>
              <option value="Naguabo">Naguabo</option>
              <option value="Naranjito">Naranjito</option>
              <option value="Orocovis">Orocovis</option>
              <option value="Patillas">Patillas</option>
              <option value="Peñuelas">Peñuelas</option>
              <option value="Ponce">Ponce</option>
              <option value="Quebradillas">Quebradillas</option>
              <option value="Rincón">Rincón</option>
              <option value="Río Grande">Río Grande</option>
              <option value="Sabana Grande">Sabana Grande</option>
              <option value="Salinas">Salinas</option>
              <option value="San Germán">San Germán</option>
              <option value="San Juan">San Juan</option>
              <option value="San Lorenzo">San Lorenzo</option>
              <option value="San Sebastián">San Sebastián</option>
              <option value="Santa Isabel">Santa Isabel</option>
              <option value="Toa Alta">Toa Alta</option>
              <option value="Toa Baja">Toa Baja</option>
              <option value="Trujillo Alto">Trujillo Alto</option>
              <option value="Utuado">Utuado</option>
              <option value="Vega Alta">Vega Alta</option>
              <option value="Vega Baja">Vega Baja</option>
              <option value="Vieques">Vieques</option>
              <option value="Villalba">Villalba</option>
              <option value="Yabucoa">Yabucoa</option>
              <option value="Yauco">Yauco</option>
            </select>
            <button
              onClick={() => navigate("/en/select-transaction")}
              style={{
                marginTop: "1.5rem",
                width: "100%",
                padding: "0.75rem",
                backgroundColor: "#0098BA",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.3s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#3AB09E";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#0098BA";
              }}
            >
              ⬅ Back to Transaction Selection
            </button>
          </QuestionCard>
        );

        case 2: {
          const clientMonthly = frequencyToMonthly(
            (formDataPurchase.income || "").toString().replace(/[^0-9.]/g, ""),
            formDataPurchase.incomeFrequency,
            formDataPurchase.hoursPerWeek
          );
        
          // NEW: Show estimated monthly for additional incomes if entered
          const additionalWorkMonthly = frequencyToMonthly(
            (formDataPurchase.additionalWorkIncome || "").toString().replace(/[^0-9.]/g, ""),
            formDataPurchase.additionalWorkIncomeFrequency,
            formDataPurchase.additionalWorkHoursPerWeek
          );
          const bonusMonthly = frequencyToMonthly(
            (formDataPurchase.bonusIncome || "").toString().replace(/[^0-9.]/g, ""),
            formDataPurchase.bonusIncomeFrequency
          );
        
                 
          return (
            <QuestionCard title="What is your income?" {...getControls()}>
              <select
                name="incomeFrequency"
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "16px",
                  marginTop: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  appearance: "none",
                }}
                value={formDataPurchase.incomeFrequency || ""}
                onChange={handleChange}
              >
                <option value="">-- Select Frequency --</option>
                <option value="Hourly">Hourly</option>
                <option value="Weekly">Weekly</option>
                <option value="Bi-Weekly">Bi-Weekly</option>
                <option value="Semi-Monthly">Semi-Monthly</option>
                <option value="Monthly">Monthly</option>
                <option value="Annually">Annually</option>
              </select>
        
              {formDataPurchase.incomeFrequency === "Hourly" ? (
                <div className="qp-row" style={rowStyle}>
                  <input
                    className="qp-input"
                    style={smallInputStyle}
                    name="income"
                    placeholder="Hourly Rate"
                    value={formDataPurchase.income || ""}
                    onChange={handleChange}
                  />
                  <input
                    className="qp-input"
                    style={smallInputStyle}
                    name="hoursPerWeek"
                    placeholder="Hours/Week"
                    value={formDataPurchase.hoursPerWeek || ""}
                    onChange={handleChange}
                  />
                </div>
              ) : (
                <input
                  style={{
                    width: "95%",
                    padding: "10px",
                    fontSize: "16px",
                    marginTop: "8px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    appearance: "none",
                  }}
                  name="income"
                  placeholder="Enter income"
                  value={formDataPurchase.income || ""}
                  onChange={handleChange}
                />
              )}
        
              {clientMonthly && (
                <div style={{ marginTop: "10px" }}>
                  <strong>Estimated Monthly Income: </strong>$
                  {Number(clientMonthly).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              )}
        
              {/* --- Additional Income Button and Fields --- */}
              <div style={{ marginTop: "18px" }}>
                {!showAdditionalIncome ? (
                  <button
                    type="button"
                    style={{
                      padding: "7px 18px",
                      borderRadius: "7px",
                      background: "#278bc4",
                      color: "#fff",
                      fontWeight: 600,
                      border: "none",
                      fontSize: "1em",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowAdditionalIncome(true)}
                  >
                    Add Additional Income
                  </button>
                ) : (
                  <>
                    <div style={{ marginTop: "14px", marginBottom: "8px" }}>
                      <b>Additional Work Income</b>
                    </div>
                    <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
                      <input
                        type="number"
                        min="0"
                        name="additionalWorkIncome"
                        placeholder="Amount"
                        style={{ width: "120px", borderRadius: 6, padding: "7px" }}
                        value={formDataPurchase.additionalWorkIncome || ""}
                        onChange={handleChange}
                      />
                      <select
                        name="additionalWorkIncomeFrequency"
                        style={{ borderRadius: 6, padding: "7px", width: "140px" }}
                        value={formDataPurchase.additionalWorkIncomeFrequency || ""}
                        onChange={handleChange}
                      >
                        <option value="">Select Frequency</option>
                        <option value="Hourly">Hourly</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Annually">Yearly</option>
                      </select>
                      {/* Hours/Week if Hourly */}
                      {formDataPurchase.additionalWorkIncomeFrequency === "Hourly" && (
                        <input
                          type="number"
                          min="0"
                          name="additionalWorkHoursPerWeek"
                          placeholder="Hours/Week"
                          style={{ width: "100px", borderRadius: 6, padding: "7px" }}
                          value={formDataPurchase.additionalWorkHoursPerWeek || ""}
                          onChange={handleChange}
                        />
                      )}
                    </div>
                    {additionalWorkMonthly ? (
                      <div style={{ marginLeft: 2, marginBottom: 8 }}>
                        <small>
                          <strong>Monthly Equivalent: </strong>$
                          {Number(additionalWorkMonthly).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </small>
                      </div>
                    ) : null}
                    <div style={{ marginTop: "10px", marginBottom: "8px" }}>
                      <b>Bonus Income</b>
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                      <input
                        type="number"
                        min="0"
                        name="bonusIncome"
                        placeholder="Amount"
                        style={{ width: "120px", borderRadius: 6, padding: "7px" }}
                        value={formDataPurchase.bonusIncome || ""}
                        onChange={handleChange}
                      />
                      <select
                        name="bonusIncomeFrequency"
                        style={{ borderRadius: 6, padding: "7px", width: "140px" }}
                        value={formDataPurchase.bonusIncomeFrequency || ""}
                        onChange={handleChange}
                      >
                        <option value="">Select Frequency</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Annually">Yearly</option>
                      </select>
                    </div>
                    {bonusMonthly ? (
                      <div style={{ marginLeft: 2, marginBottom: 10 }}>
                        <small>
                          <strong>Monthly Equivalent: </strong>$
                          {Number(bonusMonthly).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </small>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      style={{
                        marginTop: "15px",
                        color: "#ff4b00",
                        background: "none",
                        border: "none",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        setShowAdditionalIncome(false);                        
                         handleChange({ target: { name: "additionalWorkIncome", value: "" } });
                         handleChange({ target: { name: "additionalWorkIncomeFrequency", value: "" } });
                         handleChange({ target: { name: "additionalWorkHoursPerWeek", value: "" } });
                         handleChange({ target: { name: "bonusIncome", value: "" } });
                         handleChange({ target: { name: "bonusIncomeFrequency", value: "" } });
                      }}
                    >
                      Remove Additional Income
                    </button>
                  </>
                )}
              </div>
            </QuestionCard>
          );
        }

      case 3:
        return (
          <QuestionCard title="What is your marital status?" {...getControls()}>
            <select
              name="maritalStatus"
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                marginTop: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                appearance: "none",
              }}
              value={formDataPurchase.maritalStatus || ""}
              onChange={handleChange}
            >
              <option value="">-- Select --</option>
              <option value="Unmarried">Unmarried</option>
              <option value="Married">Married</option>
            </select>
          </QuestionCard>
        );
      case 4:
        return (
          <QuestionCard
            title="Will a prenuptial agreement be used?"
            {...getControls()}
          >
            <select
              name="prenuptialAgreement"
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                marginTop: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                appearance: "none",
              }}
              value={formDataPurchase.prenuptialAgreement || ""}
              onChange={handleChange}
            >
              <option value="">-- Select --</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </QuestionCard>
        );
      case 5:
        if (!includeSpouse) return null;
        return (
          <QuestionCard title="Spouse Information" {...getControls()}>
            <div className="qp-row" style={rowStyle}>
              <input
                className="qp-input"
                style={smallInputStyle}
                name="spouseFirstName"
                placeholder="First Name"
                value={formDataPurchase.spouseFirstName || ""}
                onChange={handleChange}
              />
              <input
                className="qp-input"
                style={smallInputStyle}
                name="spouseLastName"
                placeholder="Last Name"
                value={formDataPurchase.spouseLastName || ""}
                onChange={handleChange}
              />
            </div>
            <div className="qp-row" style={rowStyle}>
              <input
                className="qp-input"
                style={smallInputStyle}
                name="spousePhone"
                placeholder="Phone Number"
                value={formatPhoneNumber(formDataPurchase.spousePhone || "")}
                onChange={handleChange}
              />
              <input
                className="qp-input"
                style={smallInputStyle}
                name="spouseEmail"
                placeholder="Email Address"
                value={formDataPurchase.spouseEmail || ""}
                onChange={handleChange}
              />
            </div>
          </QuestionCard>
        );
        case 6:
          if (!includeSpouse) return null;
          const spouseMonthly = frequencyToMonthly(
            (formDataPurchase.spouseIncome || "")
              .toString()
              .replace(/[^0-9.]/g, ""),
            formDataPurchase.spouseFrequency,
            formDataPurchase.spouseHoursPerWeek
          );
        
          const spouseAdditionalWorkMonthly = frequencyToMonthly(
            (formDataPurchase.spouseAdditionalWorkIncome || "").toString().replace(/[^0-9.]/g, ""),
            formDataPurchase.spouseAdditionalWorkIncomeFrequency,
            formDataPurchase.spouseAdditionalWorkHoursPerWeek
          );
          const spouseBonusMonthly = frequencyToMonthly(
            (formDataPurchase.spouseBonusIncome || "").toString().replace(/[^0-9.]/g, ""),
            formDataPurchase.spouseBonusIncomeFrequency
          );
        
          return (
            <QuestionCard title="Spouse Current Income" {...getControls()}>
              <select
                name="spouseFrequency"
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "16px",
                  marginTop: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  appearance: "none",
                }}
                value={formDataPurchase.spouseFrequency || ""}
                onChange={handleChange}
              >
                <option value="">-- Select Frequency --</option>
                <option value="Hourly">Hourly</option>
                <option value="Weekly">Weekly</option>
                <option value="Bi-Weekly">Bi-Weekly</option>
                <option value="Semi-Monthly">Semi-Monthly</option>
                <option value="Monthly">Monthly</option>
                <option value="Annually">Annually</option>
              </select>
              {formDataPurchase.spouseFrequency === "Hourly" ? (
                <div className="qp-row" style={rowStyle}>
                  <input
                    className="qp-input"
                    style={smallInputStyle}
                    name="spouseIncome"
                    placeholder="Hourly Rate"
                    value={formDataPurchase.spouseIncome || ""}
                    onChange={handleChange}
                  />
                  <input
                    className="qp-input"
                    style={smallInputStyle}
                    name="spouseHoursPerWeek"
                    placeholder="Hours/Week"
                    value={formDataPurchase.spouseHoursPerWeek || ""}
                    onChange={handleChange}
                  />
                </div>
              ) : (
                <input
                  className="qp-input"
                  style={inputStyle}
                  name="spouseIncome"
                  placeholder="Spouse's Income"
                  value={formDataPurchase.spouseIncome || ""}
                  onChange={handleChange}
                />
              )}
              {spouseMonthly && (
                <div style={{ marginTop: "10px" }}>
                  <strong>Estimated Spouse Monthly Income: </strong>$
                  {Number(spouseMonthly || 0).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              )}
        
              {/* --- Spouse Additional Income Button and Fields --- */}
              <div style={{ marginTop: "18px" }}>
                {!showSpouseAdditionalIncome ? (
                  <button
                    type="button"
                    style={{
                      padding: "7px 18px",
                      borderRadius: "7px",
                      background: "#278bc4",
                      color: "#fff",
                      fontWeight: 600,
                      border: "none",
                      fontSize: "1em",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowSpouseAdditionalIncome(true)}
                  >
                    Add Additional Income
                  </button>
                ) : (
                  <>
                    <div style={{ marginTop: "14px", marginBottom: "8px" }}>
                      <b>Spouse Additional Work Income</b>
                    </div>
                    <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
                      <input
                        type="number"
                        min="0"
                        name="spouseAdditionalWorkIncome"
                        placeholder="Amount"
                        style={{ width: "120px", borderRadius: 6, padding: "7px" }}
                        value={formDataPurchase.spouseAdditionalWorkIncome || ""}
                        onChange={handleChange}
                      />
                      <select
                        name="spouseAdditionalWorkIncomeFrequency"
                        style={{ borderRadius: 6, padding: "7px", width: "140px" }}
                        value={formDataPurchase.spouseAdditionalWorkIncomeFrequency || ""}
                        onChange={handleChange}
                      >
                        <option value="">Select Frequency</option>
                        <option value="Hourly">Hourly</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Annually">Yearly</option>
                      </select>
                      {/* Hours/Week if Hourly */}
                      {formDataPurchase.spouseAdditionalWorkIncomeFrequency === "Hourly" && (
                        <input
                          type="number"
                          min="0"
                          name="spouseAdditionalWorkHoursPerWeek"
                          placeholder="Hours/Week"
                          style={{ width: "100px", borderRadius: 6, padding: "7px" }}
                          value={formDataPurchase.spouseAdditionalWorkHoursPerWeek || ""}
                          onChange={handleChange}
                        />
                      )}
                    </div>
                    {spouseAdditionalWorkMonthly ? (
                      <div style={{ marginLeft: 2, marginBottom: 8 }}>
                        <small>
                          <strong>Monthly Equivalent: </strong>$
                          {Number(spouseAdditionalWorkMonthly).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </small>
                      </div>
                    ) : null}
                    <div style={{ marginTop: "10px", marginBottom: "8px" }}>
                      <b>Spouse Bonus Income</b>
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                      <input
                        type="number"
                        min="0"
                        name="spouseBonusIncome"
                        placeholder="Amount"
                        style={{ width: "120px", borderRadius: 6, padding: "7px" }}
                        value={formDataPurchase.spouseBonusIncome || ""}
                        onChange={handleChange}
                      />
                      <select
                        name="spouseBonusIncomeFrequency"
                        style={{ borderRadius: 6, padding: "7px", width: "140px" }}
                        value={formDataPurchase.spouseBonusIncomeFrequency || ""}
                        onChange={handleChange}
                      >
                        <option value="">Select Frequency</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Annually">Yearly</option>
                      </select>
                    </div>
                    {spouseBonusMonthly ? (
                      <div style={{ marginLeft: 2, marginBottom: 10 }}>
                        <small>
                          <strong>Monthly Equivalent: </strong>$
                          {Number(spouseBonusMonthly).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </small>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      style={{
                        marginTop: "15px",
                        color: "#ff4b00",
                        background: "none",
                        border: "none",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        setShowSpouseAdditionalIncome(false);
                        // Optionally clear spouse additional fields:
                        handleChange({ target: { name: "spouseAdditionalWorkIncome", value: "" } });
                        handleChange({ target: { name: "spouseAdditionalWorkIncomeFrequency", value: "" } });
                        handleChange({ target: { name: "spouseAdditionalWorkHoursPerWeek", value: "" } });
                        handleChange({ target: { name: "spouseBonusIncome", value: "" } });
                        handleChange({ target: { name: "spouseBonusIncomeFrequency", value: "" } });
                      }}
                    >
                      Remove Additional Income
                    </button>
                  </>
                )}
              </div>
            </QuestionCard>
          );
      case 7:
        return (
          <QuestionCard
            title="What is your employment type?"
            {...getControls()}
          >
            <select
              name="employmentType"
              value={formDataPurchase.employmentType || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                marginTop: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                appearance: "none",
              }}
            >
              <option value="">-- Select Employment Type --</option>
              <option value="Self-Employed">Self-Employed</option>
              <option value="Hired as a Contractor">
                Hired as a Contractor
              </option>
              <option value="Hired as an Employee">Hired as an Employee</option>
              <option value="Retired">Retired</option>
              <option value="Other">Other</option>
            </select>
            {formDataPurchase.employmentType === "Other" && (
              <input
                className="qp-input"
                name="employmentOtherDetail"
                placeholder="Employment Type"
                value={formDataPurchase.employmentOtherDetail || ""}
                onChange={handleChange}
                style={inputStyle}
              />
            )}
          </QuestionCard>
        );
      case 8:
        return (
          <QuestionCard
            title="How long have you been in your current line of work?"
            {...getControls()}
          >
            <select
              name="workExperienceDuration"
              value={formDataPurchase.workExperienceDuration || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                marginTop: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                appearance: "none",
              }}
            >
              <option value="">-- Select --</option>
              <option value=">2">More than 2 years</option>
              <option value="<2">Less than 2 years</option>
            </select>
          </QuestionCard>
        );
      case 9:
        return (
          <QuestionCard
            title="What is your current occupation?"
            {...getControls()}
          >
            {formDataPurchase.workExperienceDuration === "<2" && (
              <input
                name="currentOccupation"
                placeholder="Occupation"
                value={formDataPurchase.currentOccupation || ""}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "16px",
                  marginTop: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  appearance: "none",
                }}
              />
            )}
          </QuestionCard>
        );

      case 10:
        return (
          <QuestionCard
            title="Have you been in this occupation for 2 years?"
            {...getControls()}
          >
            <select
              name="occupation2Years"
              value={formDataPurchase.occupation2Years || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                marginTop: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                appearance: "none",
              }}
            >
              <option value="">-- Select --</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </QuestionCard>
        );
      case 11:
        return (
          <QuestionCard
            title="Are you currently a student or were you a student in the past 12 months?"
            {...getControls()}
          >
            <select
              name="studentStatus"
              value={formDataPurchase.studentStatus || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                marginTop: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                appearance: "none",
              }}
            >
              <option value="">-- Select --</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </QuestionCard>
        );

      case 12:
        if (!includeSpouse) return null;
        return (
          <QuestionCard
            title="What is your spouse's employment type?"
            {...getControls()}
          >
            <select
              name="spouseEmploymentType"
              value={formDataPurchase.spouseEmploymentType || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                marginTop: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                appearance: "none",
              }}
            >
              <option value="">-- Select Spouse's Employment Type --</option>
              <option value="Self-Employed">Self-Employed</option>
              <option value="Hired as a Contractor">
                Hired as a Contractor
              </option>
              <option value="Hired as an Employee">Hired as an Employee</option>
              <option value="Retired">Retired</option>
              <option value="Other">Other</option>
            </select>
            {formDataPurchase.spouseEmploymentType === "Other" && (
              <input
                name="spouseEmploymentOtherDetail"
                placeholder="Employment Type"
                value={formDataPurchase.spouseEmploymentOtherDetail || ""}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "16px",
                  marginTop: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  appearance: "none",
                }}
              />
            )}
          </QuestionCard>
        );

      case 13:
        if (!includeSpouse) return null;
        return (
          <QuestionCard
            title="How long has your spouse been in their current line of work?"
            {...getControls()}
          >
            <select
              name="spouseWorkExperienceDuration"
              value={formDataPurchase.spouseWorkExperienceDuration || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                marginTop: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                appearance: "none",
              }}
            >
              <option value="">-- Select --</option>
              <option value=">2">More than 2 years</option>
              <option value="<2">Less than 2 years</option>
            </select>
          </QuestionCard>
        );

        case 14:
          return (
            <QuestionCard
              title={`Enter ${includeSpouse ? "all " : "your"} monthly payments`}
              {...getControls()}
            >
              {/* Always Required: Do you have debts to report? */}
              <div style={{ marginBottom: "18px", fontSize: "1.08em" }}>
                <label style={{ fontWeight: 600 }}>
                  Do you have any debts to report?
                </label>
                <div style={{ marginTop: 8 }}>
                  <label>
                    <input
                      type="radio"
                      name="hasDebts"
                      value="Yes"
                      checked={formDataPurchase.hasDebts === "Yes"}
                      onChange={e => {
                        handleChange(e);
                        // No need to reset selectedDebts; leave as-is if Yes
                      }}
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginLeft: 24 }}>
                    <input
                      type="radio"
                      name="hasDebts"
                      value="No"
                      checked={formDataPurchase.hasDebts === "No"}
                      onChange={e => {
                        handleChange(e);
                        setSelectedDebts([]);
                        setFormDataPurchase(prev => ({
                          ...prev,
                          selectedDebts: [],
                        }));
                      }}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
        
              {/* Only show debt options if they answered Yes */}
              {formDataPurchase.hasDebts === "Yes" && (
                <>
                  <div
                    className="qp-row"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      width: "100%",
                      marginBottom: "20px",
                    }}
                  >
                    {/* Checkbox section */}
                    <div
                      className="qp-checks"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "85%",
                        paddingTop: "12px",
                      }}
                    >
                      <div className="qp-check-col">
                        {["autoLoan", "personalLoan", "studentLoan"].map((item) => (
                          <label
                            key={item}
                            className="qp-check-label"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "10px",
                              whiteSpace: "nowrap",
                              fontSize: "25px",
                            }}
                          >
                            <input
                              type="checkbox"
                              value={item}
                              checked={selectedDebts.includes(item)}
                              onChange={handleDebtSelection}
                            />{" "}
                            {item === "autoLoan"
                              ? "Auto Loan"
                              : item === "personalLoan"
                              ? "Personal Loan"
                              : "Student Loan"}
                          </label>
                        ))}
                      </div>
                      <div className="qp-check-col">
                        {["creditCards", "storeCards", "furnitureLoans"].map(
                          (item) => (
                            <label
                              key={item}
                              className="qp-check-label"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "10px",
                                whiteSpace: "nowrap",
                                fontSize: "25px",
                              }}
                            >
                              <input
                                type="checkbox"
                                value={item}
                                checked={selectedDebts.includes(item)}
                                onChange={handleDebtSelection}
                              />{" "}
                              {item
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (c) => c.toUpperCase())}
                            </label>
                          )
                        )}
                      </div>
                    </div>
        
                    {/* Vertical button stack */}
                    <div
                      className="qp-btns debt-actions"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        paddingTop: "10px",
                        alignItems: "center",
                      }}
                    >
                      <div
                        onClick={() => {
                          const allOptions = [
                            "autoLoan",
                            "personalLoan",
                            "studentLoan",
                            "creditCards",
                            "storeCards",
                            "furnitureLoans",
                            "applianceLoans",
                            "otherDebts",
                          ];
                          setSelectedDebts((prev) => {
                            const allSelected = allOptions.every((option) =>
                              prev.includes(option)
                            );
                            const nextDebts = allSelected ? [] : Array.from(new Set([...prev, ...allOptions]));
                            setFormDataPurchase((fdp) => ({
                              ...fdp,
                              selectedDebts: nextDebts,
                            }));
                            return nextDebts;
                          });
                        }}
                        style={{
                          width: "30px",
                          height: "30px",
                          borderRadius: "50%",
                          backgroundColor: "#0096b2",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                        title="Select all debts"
                      >
                        All
                      </div>
        
                      <div
                        onClick={() => {
                          setPreviousSelectedDebts(selectedDebts);
                          setPreviousDebtFormData(formDataPurchase);
                          const clearedForm = { ...formDataPurchase };
                          const debtFields = [
                            "autoLoan",
                            "personalLoan",
                            "studentLoanMonthly",
                            "studentLoanTotal",
                            "creditCard1",
                            "creditCard2",
                            "creditCard3",
                            "creditCard4",
                            "storeCards",
                            "furnitureLoans",
                            "applianceLoans",
                            "otherDebts",
                            "spouseAutoLoan",
                            "spousePersonalLoan",
                            "spouseStudentLoanMonthly",
                            "spouseStudentLoanTotal",
                            "spouseCreditCard1",
                            "spouseCreditCard2",
                            "spouseCreditCard3",
                            "spouseCreditCard4",
                            "spouseStoreCards",
                            "spouseFurnitureLoans",
                            "spouseApplianceLoans",
                            "spouseOtherDebts",
                          ];
                          debtFields.forEach((field) => {
                            clearedForm[field] = "";
                          });
                          clearedForm.selectedDebts = [];
                          setFormDataPurchase(clearedForm);
                          setSelectedDebts([]);
                        }}
                        style={{
                          width: "30px",
                          height: "30px",
                          borderRadius: "50%",
                          backgroundColor: "#0096b2",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          fontSize: "16px",
                        }}
                        title="Clear all selections"
                      >
                        🗑
                      </div>
        
                      <div
                        onClick={() => {
                          setFormDataPurchase(previousDebtFormData);
                          setSelectedDebts(previousDebtFormData.selectedDebts || []);
                        }}
                        style={{
                          width: "30px",
                          height: "30px",
                          borderRadius: "50%",
                          backgroundColor: "#019261",
                          color: "black",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                        title="Undo clear"
                      >
                        ↩
                      </div>
                    </div>
                  </div>
        
                  <div
                    style={{
                      textAlign: "center",
                      marginTop: "10px",
                      fontSize: "20px",
                    }}
                  >
                    <label>
                      <input
                        type="checkbox"
                        value="otherDebts"
                        checked={selectedDebts.includes("otherDebts")}
                        onChange={handleDebtSelection}
                      />{" "}
                      Other
                    </label>
                  </div>
        
                  {/* Input groups by condition */}
                  {selectedDebts.includes("autoLoan") && (
                    <div className="qp-row" style={rowStyle}>
                      <input
                        className="qp-input"
                        name="autoLoan"
                        value={formDataPurchase.autoLoan || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        style={smallInputStyle}
                        placeholder="Your Auto Loan"
                      />
                      {includeSpouse && (
                        <input
                          className="qp-input"
                          name="spouseAutoLoan"
                          value={formDataPurchase.spouseAutoLoan || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          style={smallInputStyle}
                          placeholder="Spouse's Auto Loan"
                        />
                      )}
                    </div>
                  )}
        
                  {selectedDebts.includes("personalLoan") && (
                    <div className="qp-row" style={rowStyle}>
                      <input
                        className="qp-input"
                        name="personalLoan"
                        value={formDataPurchase.personalLoan || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        style={smallInputStyle}
                        placeholder="Your Personal Loan"
                      />
                      {includeSpouse && (
                        <input
                          className="qp-input"
                          name="spousePersonalLoan"
                          value={formDataPurchase.spousePersonalLoan || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          style={smallInputStyle}
                          placeholder="Spouse's Personal Loan"
                        />
                      )}
                    </div>
                  )}
        
                  {selectedDebts.includes("studentLoan") && (
                    <>
                      <div className="qp-row" style={rowStyle}>
                        <input
                          className="qp-input"
                          name="studentLoanMonthly"
                          value={formDataPurchase.studentLoanMonthly || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          style={smallInputStyle}
                          placeholder="Your Student Loan Payment"
                        />
                        {includeSpouse && (
                          <input
                            className="qp-input"
                            name="spouseStudentLoanMonthly"
                            value={formDataPurchase.spouseStudentLoanMonthly || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            style={smallInputStyle}
                            placeholder="Spouse's Student Loan Payment"
                          />
                        )}
                      </div>
                      {(!formDataPurchase.studentLoanMonthly ||
                        parseFloat(
                          formDataPurchase.studentLoanMonthly.replace(/[^0-9.]/g, "")
                        ) === 0) && (
                        <div className="qp-row" style={rowStyle}>
                          <input
                            className="qp-input"
                            name="studentLoanTotal"
                            value={formDataPurchase.studentLoanTotal || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            style={smallInputStyle}
                            placeholder="Your Student Loan Balance"
                          />
                          {includeSpouse && (
                            <input
                              className="qp-input"
                              name="spouseStudentLoanTotal"
                              value={formDataPurchase.spouseStudentLoanTotal || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              style={smallInputStyle}
                              placeholder="Spouse's Student Loan Balance"
                            />
                          )}
                        </div>
                      )}
                    </>
                  )}
        
                  {selectedDebts.includes("creditCards") && (
                    <>
                      <div className="qp-row" style={rowStyle}>
                        <input
                          className="qp-input"
                          name="creditCard1"
                          value={formDataPurchase.creditCard1 || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          style={smallInputStyle}
                          placeholder="Credit Card 1"
                        />
                        <input
                          className="qp-input"
                          name="creditCard2"
                          value={formDataPurchase.creditCard2 || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          style={smallInputStyle}
                          placeholder="Credit Card 2"
                        />
                      </div>
                      <div className="qp-row" style={rowStyle}>
                        <input
                          className="qp-input"
                          name="creditCard3"
                          value={formDataPurchase.creditCard3 || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          style={smallInputStyle}
                          placeholder="Credit Card 3"
                        />
                        <input
                          className="qp-input"
                          name="creditCard4"
                          value={formDataPurchase.creditCard4 || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          style={smallInputStyle}
                          placeholder="Credit Card 4"
                        />
                      </div>
                    </>
                  )}
        
                  {selectedDebts.includes("storeCards") && (
                    <div className="qp-row" style={rowStyle}>
                      <input
                        className="qp-input"
                        name="storeCards"
                        value={formDataPurchase.storeCards || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        style={smallInputStyle}
                        placeholder="Your Store Cards"
                      />
                      {includeSpouse && (
                        <input
                          className="qp-input"
                          name="spouseStoreCards"
                          value={formDataPurchase.spouseStoreCards || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          style={smallInputStyle}
                          placeholder="Spouse's Store Cards"
                        />
                      )}
                    </div>
                  )}
        
                  {selectedDebts.includes("furnitureLoans") && (
                    <div className="qp-row" style={rowStyle}>
                      <input
                        className="qp-input"
                        name="furnitureLoans"
                        value={formDataPurchase.furnitureLoans || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        style={smallInputStyle}
                        placeholder="Your Furniture Loans"
                      />
                      {includeSpouse && (
                        <input
                          className="qp-input"
                          name="spouseFurnitureLoans"
                          value={formDataPurchase.spouseFurnitureLoans || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          style={smallInputStyle}
                          placeholder="Spouse's Furniture Loans"
                        />
                      )}
                    </div>
                  )}
        
                  {selectedDebts.includes("otherDebts") && (
                    <input
                      className="qp-input"
                      name="otherDebts"
                      value={formDataPurchase.otherDebts || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      style={inputStyle}
                      placeholder="Other Monthly Debts"
                    />
                  )}
        
                  <div style={{ marginTop: "20px", fontWeight: "bold" }}>
                    Total Monthly Payment: $
                    {(() => {
                      const toNum = (v) =>
                        parseFloat((v || "").toString().replace(/[^0-9.]/g, "")) || 0;
        
                      const studentLoanMonthly = toNum(
                        formDataPurchase.studentLoanMonthly
                      );
                      const studentLoanTotal = toNum(
                        formDataPurchase.studentLoanTotal
                      );
                      const studentLoanCalc =
                        studentLoanMonthly || studentLoanTotal * 0.005;
        
                      const spouseStudentLoanMonthly = toNum(
                        formDataPurchase.spouseStudentLoanMonthly
                      );
                      const spouseStudentLoanTotal = toNum(
                        formDataPurchase.spouseStudentLoanTotal
                      );
                      const spouseStudentLoanCalc =
                        spouseStudentLoanMonthly || spouseStudentLoanTotal * 0.005;
        
                      const totalDebt =
                        toNum(formDataPurchase.autoLoan) +
                        toNum(formDataPurchase.personalLoan) +
                        studentLoanCalc +
                        toNum(formDataPurchase.creditCard1) +
                        toNum(formDataPurchase.creditCard2) +
                        toNum(formDataPurchase.creditCard3) +
                        toNum(formDataPurchase.creditCard4) +
                        toNum(formDataPurchase.storeCards) +
                        toNum(formDataPurchase.furnitureLoans) +
                        toNum(formDataPurchase.applianceLoans) +
                        toNum(formDataPurchase.otherDebts) +
                        toNum(formDataPurchase.spouseAutoLoan) +
                        toNum(formDataPurchase.spousePersonalLoan) +
                        spouseStudentLoanCalc +
                        toNum(formDataPurchase.spouseCreditCard1) +
                        toNum(formDataPurchase.spouseCreditCard2) +
                        toNum(formDataPurchase.spouseCreditCard3) +
                        toNum(formDataPurchase.spouseCreditCard4) +
                        toNum(formDataPurchase.spouseStoreCards) +
                        toNum(formDataPurchase.spouseFurnitureLoans) +
                        toNum(formDataPurchase.spouseApplianceLoans) +
                        toNum(formDataPurchase.spouseOtherDebts);
        
                      return totalDebt.toFixed(2);
                    })()}
                  </div>
                </>
              )}
            </QuestionCard>
          );

      case 15:
        const creditScoreOptions = [
          "Less than 620",
          ...Array.from(
            { length: Math.floor((760 - 620) / 5) + 1 },
            (_, i) => `${620 + i * 5}`
          ),
          "Greater than 760",
        ];

        const selectStyle = {
          width: "100%",
          padding: "10px",
          fontSize: "16px",
          marginTop: "8px",
          borderRadius: "6px",
          border: "1px solid #ccc",
          appearance: "none", //removes default arrow styling
        };

        return (
          <QuestionCard
            title={`Enter ${
              includeSpouse ? "your and your spouse's" : "your"
            } credit score`}
            {...getControls()}
          >
            <div style={{ width: "100%" }}>
              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontSize: "16px", fontWeight: "bold" }}>
                  Your Credit Score:
                </label>
                <select
                  name="creditScore"
                  value={formDataPurchase.creditScore || ""}
                  onChange={handleChange}
                  size={1}
                  style={selectStyle}
                >
                  <option value="">-- Select --</option>
                  {creditScoreOptions.map((score) => (
                    <option key={score} value={score}>
                      {score}
                    </option>
                  ))}
                </select>
              </div>

              {includeSpouse && (
                <div style={{ marginBottom: "20px" }}>
                  <label style={{ fontSize: "16px", fontWeight: "bold" }}>
                    Spouse's Credit Score:
                  </label>
                  <select
                    name="spouseCreditScore"
                    value={formDataPurchase.spouseCreditScore || ""}
                    onChange={handleChange}
                    size={1}
                    style={selectStyle}
                  >
                    <option value="">-- Select --</option>
                    {creditScoreOptions.map((score) => (
                      <option key={score} value={score}>
                        {score}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </QuestionCard>
        );
      case 16:
        return (
          <QuestionCard
            title={
              <div style={{ textAlign: "justify" }}>
                {`Have ${
                  includeSpouse ? "you or your spouse" : "you"
                } declared bankruptcy or had a foreclosure in the last 7 years?`}
              </div>
            }
            {...getControls()}
          >
            <div style={{ width: "100%" }}>
              <select
                name="bankruptcyOrForeclosure"
                value={formDataPurchase.bankruptcyOrForeclosure || ""}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "16px",
                  marginTop: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  appearance: "none",
                }}
              >
                <option value="">-- Select --</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>

              {formDataPurchase.bankruptcyOrForeclosure === "Yes" && (
                <div style={{ marginTop: "20px" }}>
                  <label style={{ fontWeight: "bold" }}>
                    Type of Bankruptcy:
                  </label>
                  <select
                    name="bankruptcyType"
                    value={formDataPurchase.bankruptcyType || ""}
                    onChange={handleChange}
                    style={{
                      width: "100%",
                      padding: "10px",
                      fontSize: "16px",
                      marginTop: "8px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      appearance: "none",
                    }}
                  >
                    <option value="">-- Select --</option>
                    <option value="Chapter 13">Chapter 13</option>
                    <option value="Chapter 7">Chapter 7</option>
                  </select>

                  <div style={{ marginTop: "20px" }}>
                    <label style={{ fontWeight: "bold" }}>
                      Have you reestablished your credit history?
                      <span
                        onClick={() => setShowExplanation(!showExplanation)}
                        style={{
                          marginLeft: "8px",
                          cursor: "pointer",
                          borderRadius: "50%",
                          width: "18px",
                          height: "18px",
                          display: "inline-block",
                          backgroundColor: "#0096b2",
                          color: "white",
                          textAlign: "center",
                          lineHeight: "18px",
                          fontSize: "12px",
                        }}
                      >
                        ?
                      </span>
                    </label>
                    {showExplanation && (
                      <div
                        style={{
                          backgroundColor: "white",
                          color: "black",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          padding: "10px",
                          marginTop: "8px",
                          fontSize: "14px",
                          maxWidth: "400px",
                          textAlign: "justify",
                        }}
                      >
                        Reestablishing your credit report means having 3 debts
                        on your credit report post your Bankruptcy that have
                        been paid on time for the last 12 months.
                      </div>
                    )}
                    <select
                      name="reestablishedCredit"
                      value={formDataPurchase.reestablishedCredit || ""}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "10px",
                        fontSize: "16px",
                        marginTop: "8px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        appearance: "none",
                      }}
                    >
                      <option value="">-- Select --</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </QuestionCard>
        );
      case 17:
        return (
          <QuestionCard
            title={
              <div style={{ textAlign: "justify" }}>
                {includeSpouse
                  ? "Did you or your spouse ever serve, or are you currently serving, in the United States Armed Forces?"
                  : "Did you (or your deceased spouse) ever serve, or are you currently serving, in the United States Armed Forces?"}
              </div>
            }
            {...getControls()}
          >
            <div style={{ width: "100%" }}>
              <select
                name="veteranStatus"
                value={formDataPurchase.veteranStatus || ""}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "16px",
                  marginTop: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  appearance: "none",
                }}
              >
                <option value="">-- Select --</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </QuestionCard>
        );
      case 18:
        return (
          <QuestionCard
            title="Have you filed your state tax returns for the last two years?"
            {...getControls()}
          >
            <div style={{ width: "100%" }}>
              <select
                name="stateTaxesFiled"
                value={formDataPurchase.stateTaxesFiled || ""}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "16px",
                  marginTop: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  appearance: "none",
                }}
              >
                <option value="">-- Select --</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </QuestionCard>
        );
      case 19:
        return (
          <QuestionCard
            title="Do you already have a property you want to purchase?"
            {...getControls()}
          >
            <div style={{ width: "100%" }}>
              <div style={{ marginBottom: "20px" }}>
                <select
                  name="hasPropertyToPurchase"
                  value={formDataPurchase.hasPropertyToPurchase || ""}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "10px",
                    fontSize: "16px",
                    marginTop: "8px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    appearance: "none",
                  }}
                >
                  <option value="">-- Select --</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {formDataPurchase.hasPropertyToPurchase === "Yes" && (
                <div style={{ marginTop: "20px" }}>
                  <h4>Sales Price</h4>
                  <input
                    name="salesPrice"
                    value={formDataPurchase.salesPrice || ""}
                    onChange={handleChange}
                    placeholder="Enter sales price"
                    style={{
                      width: "95%",
                      padding: "10px",
                      fontSize: "16px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      marginBottom: "20px",
                    }}
                  />

                  <h4>Street Address</h4>
                  <input
                    name="propertyStreet"
                    value={formDataPurchase.propertyStreet || ""}
                    onChange={handleChange}
                    placeholder="Enter street address"
                    style={{
                      width: "95%",
                      padding: "10px",
                      fontSize: "16px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      marginBottom: "10px",
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      gap: "30px",
                      marginBottom: "20px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h4>County</h4>
                      <input
                        name="propertyCounty"
                        value={formDataPurchase.propertyCounty || ""}
                        onChange={handleChange}
                        placeholder="Enter county"
                        style={{
                          width: "95%",
                          padding: "10px",
                          fontSize: "16px",
                          borderRadius: "6px",
                          border: "1px solid #ccc",
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4>State</h4>
                      <input
                        name="propertyState"
                        value={formDataPurchase.propertyState || "Puerto Rico"}
                        onChange={handleChange}
                        style={{
                          width: "89%",
                          padding: "10px",
                          fontSize: "16px",
                          borderRadius: "6px",
                          border: "1px solid #ccc",
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ flex: 1, marginTop: "-8px" }}>
                    <h4>Zip Code</h4>
                    <input
                      name="propertyZip"
                      value={formDataPurchase.propertyZip || ""}
                      onChange={handleChange}
                      placeholder="Zip Code"
                      style={{
                        width: "95%",
                        padding: "10px",
                        fontSize: "16px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </QuestionCard>
        );

      case 20:
        return (
          <QuestionCard
            title="Submit Application"
            onBack={getControls().onBack}
            onContinue={async () => {
              try {
                const monthlyIncome = frequencyToMonthly(
                  formDataPurchase.income,
                  formDataPurchase.incomeFrequency,
                  parseFloat(formDataPurchase.hoursPerWeek) || 40
                );

                const spouseMonthlyIncome =
                  formDataPurchase.maritalStatus === "Married" &&
                  formDataPurchase.prenuptialAgreement !== "Yes"
                    ? frequencyToMonthly(
                        formDataPurchase.spouseIncome,
                        formDataPurchase.spouseFrequency,
                        parseFloat(formDataPurchase.spouseHoursPerWeek) || 40
                      )
                    : 0;

                const toNum = (v) =>
                  parseFloat((v || "").toString().replace(/[^0-9.]/g, "")) || 0;

                const studentLoanCalc =
                  toNum(formDataPurchase.studentLoanMonthly) ||
                  toNum(formDataPurchase.studentLoanTotal) * 0.005;

                const spouseStudentLoanCalc =
                  toNum(formDataPurchase.spouseStudentLoanMonthly) ||
                  toNum(formDataPurchase.spouseStudentLoanTotal) * 0.005;

                const totalDebts = (
                  toNum(formDataPurchase.autoLoan) +
                  toNum(formDataPurchase.personalLoan) +
                  studentLoanCalc +
                  toNum(formDataPurchase.creditCard1) +
                  toNum(formDataPurchase.creditCard2) +
                  toNum(formDataPurchase.creditCard3) +
                  toNum(formDataPurchase.creditCard4) +
                  toNum(formDataPurchase.storeCards) +
                  toNum(formDataPurchase.furnitureLoans) +
                  toNum(formDataPurchase.applianceLoans) +
                  toNum(formDataPurchase.otherDebts) +
                  toNum(formDataPurchase.spouseAutoLoan) +
                  toNum(formDataPurchase.spousePersonalLoan) +
                  spouseStudentLoanCalc +
                  toNum(formDataPurchase.spouseCreditCard1) +
                  toNum(formDataPurchase.spouseCreditCard2) +
                  toNum(formDataPurchase.spouseCreditCard3) +
                  toNum(formDataPurchase.spouseCreditCard4) +
                  toNum(formDataPurchase.spouseStoreCards) +
                  toNum(formDataPurchase.spouseFurnitureLoans) +
                  toNum(formDataPurchase.spouseApplianceLoans) +
                  toNum(formDataPurchase.spouseOtherDebts)
                ).toFixed(2);

                const updatedFormData = {
                  ...formDataPurchase,
                  income: monthlyIncome,
                  spouseIncome: spouseMonthlyIncome,
                  totalDebts: parseFloat(totalDebts),
                };

                // Remove hoursPerWeek if not needed
                if (updatedFormData.incomeFrequency !== "Hourly") {
                  delete updatedFormData.hoursPerWeek;
                }

                if (
                  updatedFormData.maritalStatus === "Married" &&
                  updatedFormData.prenuptialAgreement !== "Yes" &&
                  updatedFormData.spouseFrequency !== "Hourly"
                ) {
                  delete updatedFormData.spouseHoursPerWeek;
                }

                // Strip refinance-only fields before storing purchase version
                const {
                  AutoLoanBalance,
                  CreditCardBalance,
                  MortgageBalance,
                  consolidatedFields,
                  totalLoanRequested,
                  spouseAutoLoanMonthly,
                  spousePersonalLoanMonthly,
                  ...purchaseCleaned
                } = updatedFormData;

                await updateDoc(docRef.current, {
                  formDataPurchase: {
                    ...purchaseCleaned,
                    completed: true,
                  },
                  transactionType: "Purchase",
                  currentStepQ: 20,
                  updatedAt: new Date(),
                });

                const result = calculatePreQualification(updatedFormData);

                if (result.grantEligible) {
                  result.grantPricing = {};
                  for (const loanType of result.eligibleProducts) {
                    result.grantPricing[loanType] = result.pricing[loanType];
                  }
                }

                const finalPricing = result.grantEligible
                  ? result.grantPricing
                  : result.pricing;

                const hasValidPricing = Object.values(finalPricing).some(
                  (val) => typeof val === "number" && val > 0
                );

                if (!hasValidPricing) {
                  console.warn(
                    "❌ No qualifying loan options. Skipping PDF generation."
                  );
                  return navigate("/en/dashboard");
                }

                // Pass adjusted pricing into the PDF generator
                const letters = await generatePreQualpdf(
                  { ...result, pricing: finalPricing },
                  updatedFormData
                );

                await uploadAndStorePreQualPDFs(docId, letters, "Purchase");
                navigate("/en/dashboard");
              } catch (err) {
                console.error("Failed to submit and generate PDFs:", err);
                alert("Something went wrong. Please try again.");
              }
            }}
          >
            <p style={{ textAlign: "center", fontSize: "16px" }}>
              Please review your answers before submitting. Once submitted, your
              prequalification letters will be generated.
            </p>
          </QuestionCard>
        );

      default:
        return (
          <div style={{ padding: "40px", textAlign: "center" }}>
            Step {step} content goes here.
          </div>
        );
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <div style={{ position: "absolute", top: 16, right: 16, zIndex: 10 }}>
        <LanguageToggle />
      </div>
      {renderStep()}
    </div>
  );
};
export default QuestionsPage;
